import React, { memo, useCallback, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon, Input, Link, Modal, Textarea, useTheme } from '@Sage/hoi-poi-ui';

import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const LANGUAGE_LITERALS = {
    br: 'cfm_label_br',
    de: 'cfm_label_de',
    en: 'cfm_label_en_uk',
    es: 'cfm_label_spanish',
    fr: 'cfm_label_fr',
    it: 'cfm_label_it',
    pt: 'cfm_label_pt',
    ru: 'cfm_label_ru',
    us: 'cfm_label_en_us',
};

const propTypes = {
    autoFocus: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    fieldId: PropTypes.string,
    hidden: PropTypes.bool,
    hint: PropTypes.string,
    label: PropTypes.string,
    languages: PropTypes.array,
    limitInput: PropTypes.bool,
    maxLength: PropTypes.number,
    multiLine: PropTypes.bool,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    rows: PropTypes.number,
    rowsMax: PropTypes.number,
    shouldRenderField: PropTypes.func,
    value: PropTypes.object,
};

// Helper to ensure value is always a string
const getStringValue = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val;
    return '';
};

// Helper to extract error for a specific language
const getErrorForLang = (error, lang) => {
    if (!error) return undefined;
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && lang) return error[lang] || undefined;
    return undefined;
};

const Translations = ({
    autoFocus,
    error,
    fieldId,
    hidden,
    hint,
    modalLabel,
    languages = [],
    maxLength,
    multiLine,
    onChange,
    readOnly,
    rows = 3,
    shouldRenderField,
    value = {},
    isEnriched = false,
}) => {
    const theme = useTheme();
    const mainLang = languages?.[0];
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [innerValue, setInnerValue] = useState(value || {});
    const [mainText, setMainText] = useState(getStringValue(value?.[mainLang]));
    // Track the main text from external value to detect external changes
    const externalMainText = useRef(getStringValue(value?.[mainLang]));

    // Sync external value changes only when external mainLang value changes
    useEffect(() => {
        const newExternalMainText = getStringValue(value?.[mainLang]);
        // Only sync if external value actually changed (not from our own blur)
        if (newExternalMainText !== externalMainText.current) {
            externalMainText.current = newExternalMainText;
            setMainText(newExternalMainText);
            setInnerValue(value || {});
        }
    }, [mainLang, value]);

    // Main field change handler
    const onMainChange = useCallback((newText) => {
        setMainText(newText);
    }, []);

    const onMainBlur = useCallback(() => {
        const currentMainText = getStringValue(value?.[languages[0]]);
        // Only trigger onChange if value actually changed
        if (currentMainText !== mainText) {
            // Update our ref so we don't re-sync this value
            externalMainText.current = mainText;
            const newValue = { ...value, [languages[0]]: mainText };
            onChange?.(newValue);
        }
    }, [languages, mainText, onChange, value]);

    // Modal handlers
    const openModal = useCallback(() => {
        setInnerValue(value);
        setIsModalOpen(true);
    }, [value]);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const onModalSave = useCallback(() => {
        const newMainText = getStringValue(innerValue?.[languages?.[0]]);
        externalMainText.current = newMainText;
        setMainText(newMainText);
        onChange?.(innerValue);
        setIsModalOpen(false);
    }, [innerValue, languages, onChange]);

    // Modal field change handler
    const onModalFieldChange = useCallback(
        (lang) => (newText) => {
            setInnerValue((prev) => ({ ...prev, [lang]: newText }));
        },
        [],
    );

    if (hidden) return null;
    if (shouldRenderField && !shouldRenderField(fieldId)) return null;

    const InputComponent = multiLine ? Textarea : Input;
    const placeholder = hint || getLiteral('placeholder_text_field');

    return (
        <div className="fm-field-translations">
            <InputComponent
                autoFocus={autoFocus}
                className="fm-field-translations__input"
                error={getErrorForLang(error, mainLang)}
                isFullWidth
                isReadOnly={readOnly}
                maxCharacters={maxLength}
                onBlur={onMainBlur}
                onChange={onMainChange}
                placeholder={placeholder}
                rows={multiLine ? rows : undefined}
                value={mainText}
                isEnriched={isEnriched}
            />
            <div className="fm-field-translations__action">
                <Link type="caption" variation="primary" isDisabled={readOnly} onClick={openModal}>
                    {getLiteral('action_show_languages')}
                </Link>
            </div>
            <Modal
                className="fm-translations-modal"
                cancelText={getLiteral('action_cancel')}
                confirmText={getLiteral('action_save')}
                isOpen={isModalOpen}
                onCancel={closeModal}
                onConfirm={onModalSave}
                onRequestClose={closeModal}
                size="large"
                title={modalLabel}
            >
                <div className="fm-translations-modal__container">
                    {languages.map((lang) => {
                        const langCode = lang.split('_')?.[1] || 'es';
                        const label = getLiteral(
                            LANGUAGE_LITERALS[langCode] || LANGUAGE_LITERALS.es,
                        );

                        return (
                            <div className="fm-translations-modal__field" key={lang}>
                                <InputComponent
                                    error={getErrorForLang(error, lang)}
                                    isFullWidth
                                    isReadOnly={readOnly}
                                    label={label}
                                    maxCharacters={maxLength}
                                    onChange={onModalFieldChange(lang)}
                                    placeholder={placeholder}
                                    rows={multiLine ? rows : undefined}
                                    value={getStringValue(innerValue?.[lang])}
                                />
                            </div>
                        );
                    })}
                </div>
            </Modal>
        </div>
    );
};

Translations.displayName = 'Translations';
Translations.propTypes = propTypes;

export default memo(Translations);
